import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function ConsensusRace() {
  const canvasRef = useRef(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [scores, setScores] = useState({
    PoW: 0,
    PoS: 0,
    DPoS: 0,
    BFT: 0
  });

  const mechanisms = [
    { name: 'PoW', color: '#ef4444', speed: 1.5, description: 'Proof of Work (Slowest but Secure)' },
    { name: 'PoS', color: '#3b82f6', speed: 2.5, description: 'Proof of Stake (Fast & Green)' },
    { name: 'DPoS', color: '#10b981', speed: 3, description: 'Delegated PoS (Fastest)' },
    { name: 'BFT', color: '#f59e0b', speed: 2.8, description: 'Byzantine Fault Tolerant' }
  ];

  useEffect(() => {
    if (!gameStarted) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    let positions = [0, 0, 0, 0];
    let animationId;

    const animate = () => {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, width, height);

      // Draw track lines
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 1;
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(0, 60 + i * 60);
        ctx.lineTo(width, 60 + i * 60);
        ctx.stroke();
      }

      // Update positions and draw racers
      mechanisms.forEach((mech, index) => {
        positions[index] += mech.speed;

        // Draw racer
        ctx.fillStyle = mech.color;
        ctx.fillRect(positions[index], 40 + index * 60, 30, 40);

        // Draw label
        ctx.fillStyle = '#e2e8f0';
        ctx.font = '14px Arial';
        ctx.fillText(mech.name, positions[index] + 35, 75 + index * 60);

        // Check if finished
        if (positions[index] > width && !winner) {
          setWinner(mech.name);
          setGameOver(true);
          setScores(prev => ({
            ...prev,
            [mech.name]: 1
          }));
          return;
        }
      });

      if (!gameOver) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [gameStarted, gameOver, winner]);

  const restartRace = () => {
    setGameStarted(false);
    setGameOver(false);
    setWinner(null);
    setTimeout(() => setGameStarted(true), 500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">🏁 Consensus Race</h1>
          <p className="text-gray-300">Watch different consensus mechanisms compete in real-time!</p>
        </div>

        {/* Canvas */}
        <Card className="p-6 bg-slate-800 border-slate-700 mb-8">
          <canvas
            ref={canvasRef}
            width={800}
            height={300}
            className="w-full border border-slate-600 rounded bg-slate-900"
          />
        </Card>

        {/* Mechanism Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {mechanisms.map((mech) => (
            <Card key={mech.name} className="p-4 bg-slate-700 border-slate-600">
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: mech.color }}
                />
                <h3 className="font-bold text-white">{mech.name}</h3>
              </div>
              <p className="text-sm text-gray-300 mb-3">{mech.description}</p>
              <p className="text-xs text-gray-400">Speed: {mech.speed}x</p>
            </Card>
          ))}
        </div>

        {/* Winner Display */}
        {winner && (
          <Card className="p-6 bg-gradient-to-r from-green-900 to-green-800 border-green-600 mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">🏆 {winner} Wins!</h2>
            <p className="text-green-200">Fastest consensus mechanism selected!</p>
          </Card>
        )}

        {/* Controls */}
        <div className="flex gap-4 justify-center">
          {!gameStarted && !gameOver && (
            <Button onClick={() => setGameStarted(true)} size="lg" className="bg-blue-600 hover:bg-blue-700">
              🏁 Start Race
            </Button>
          )}
          {gameOver && (
            <Button onClick={restartRace} size="lg" className="bg-blue-600 hover:bg-blue-700">
              🔄 Race Again
            </Button>
          )}
          <Button onClick={() => window.location.href = '/games'} size="lg" variant="outline">
            Back to Games
          </Button>
        </div>

        {/* Info */}
        <Card className="mt-8 p-6 bg-slate-700 border-slate-600">
          <h3 className="text-lg font-bold text-white mb-3">💡 About Consensus Mechanisms</h3>
          <ul className="space-y-2 text-gray-300 text-sm">
            <li><strong>PoW:</strong> Energy-intensive but highly secure. Bitcoin uses this.</li>
            <li><strong>PoS:</strong> Energy-efficient alternative. Ethereum switched to this in 2022.</li>
            <li><strong>DPoS:</strong> Token holders vote for validators. Faster than PoS.</li>
            <li><strong>BFT:</strong> Instant finality but requires known validators. Requires n ≥ 3f+1.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
